import React, { useCallback, useEffect, useState } from 'react';
import { Col, Image, Modal, Row, Typography, message } from 'antd';
import styled from '@emotion/styled';
import { CloseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { SpinLoading } from '../../atoms';
import { FormHandicap, FormInsights, FormJustIn, FormScore, FormTarget } from './form';
import { TitleModal } from '../../molecules/modal/TitleModal';
import { JustInImage } from '../../../assets';
import { HOME } from '../../../constants/define/translation';
import {
  HomeWidgetsRecord,
  postCreateHomeWidgetsRepository,
  postUpdateHomeWidgetsRepository,
} from '../../../repository/mytm/home/homeWidgetsRepository';
import { FormCard, HomeWidgetsSubmitForm } from '../../../types/home';
import { logging } from '../../../utils/log';
import { toCategoryFormByHomeType, toPayloadHomeWidgets } from '../../../utils/transfer/home';
import { appStyles } from '../../../styles';
import { WIDTH_MODAL } from '../../../constants/common';

const { Title } = Typography;

interface HomeTilesModalProps {
  isOpenModal: boolean;
  onClose: () => void;
  onRefresh: () => void;
  record?: HomeWidgetsRecord;
}

interface TitleModalInterface {
  title: string;
  explain: string;
  isBtnBack: boolean;
}

export const HomeTilesModal = (props: HomeTilesModalProps) => {
  const { t } = useTranslation(HOME);
  const { isOpenModal, onClose, onRefresh, record } = props;
  const [isLoading, setIsLoading] = useState(false);

  const TITLE_SELECT_CARD = {
    title: t('modal.titleCreate'),
    explain: t('modal.chooseCard'),
    isBtnBack: false,
  };

  const TITLE_FORM_SAVE_CARD_COMMON = {
    explain: t('modal.template'),
    isBtnBack: true,
  };

  const TITLE_EACH_FORM = {
    [`${FormCard.PROMOTION}`]: t('modal.promotion'),
    [`${FormCard.JUST_IN}`]: t('modal.justIn'),
    [`${FormCard.INSIGHTS}`]: t('modal.insights'),
    [`${FormCard.HANDICAP}`]: t('modal.handicap'),
    [`${FormCard.SCORE}`]: t('modal.scores'),
    [`${FormCard.TARGET}`]: t('modal.target'),
  };

  useEffect(() => {
    if (record && isOpenModal) {
      const stage = toCategoryFormByHomeType(record);
      setFromCardStage(stage);
      setTitleModal(old => ({
        ...old,
        title: TITLE_EACH_FORM[stage],
        ...TITLE_FORM_SAVE_CARD_COMMON,
        isBtnBack: false,
      }));
    }
  }, [record, isOpenModal]);

  const [formCardStage, setFromCardStage] = useState<FormCard>(FormCard.TEMPLATE);
  const [titleModal, setTitleModal] = useState<TitleModalInterface>(TITLE_SELECT_CARD);

  const onOpenModalFormCard = useCallback((stage: FormCard) => {
    setFromCardStage(stage);
    setTitleModal(old => ({
      ...old,
      title: TITLE_EACH_FORM[stage],
      ...TITLE_FORM_SAVE_CARD_COMMON,
    }));
  }, []);

  const onResetFormCard = useCallback(() => {
    setTitleModal(TITLE_SELECT_CARD);
    setFromCardStage(FormCard.TEMPLATE);
  }, []);

  const onSaveHomeWidgets = useCallback(
    async (values: HomeWidgetsSubmitForm) => {
      try {
        setIsLoading(true);
        const payload = toPayloadHomeWidgets(values);
        if (record) {
          await postUpdateHomeWidgetsRepository(record.id, payload);
          message.success(t('message.updateHomeTilesSuccess'));
        } else {
          await postCreateHomeWidgetsRepository({
            ...payload,
            extraData: JSON.stringify(values.extraData),
          });
          message.success(t('message.createHomeTilesSuccess'));
        }
        setIsLoading(false);
        onClose();
        onRefresh();
        onResetFormCard();
      } catch (error: any) {
        setIsLoading(false);
        logging(error);
        if (record) {
          message.error(error?.errorMessage || t('message.updateHomeTilesError'));
        } else {
          message.error(error?.errorMessage || t('message.createHomeTilesError'));
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onRefresh, record, onClose, onResetFormCard],
  );

  return (
    <StyledModal
      title={<TitleModal {...titleModal} onClick={onResetFormCard} />}
      centered
      visible={isOpenModal}
      width={WIDTH_MODAL}
      footer={null}
      onCancel={() => {
        onClose();
        onResetFormCard();
      }}
      maskClosable={false}
      closeIcon={<CloseCircleOutlined />}
    >
      {formCardStage === FormCard.TEMPLATE && (
        <div className="site-card-wrapper">
          <Row gutter={16}>
            <Col span={8} className="card-item">
              <Image
                src={JustInImage}
                width={200}
                preview={false}
                onClick={() => onOpenModalFormCard(FormCard.JUST_IN)}
              />
              <Title level={4}>{t('modal.justIn')}</Title>
            </Col>
            <Col span={8} className="card-item">
              <Image
                src={JustInImage}
                width={200}
                preview={false}
                onClick={() => onOpenModalFormCard(FormCard.INSIGHTS)}
              />
              <Title level={4}>{t('modal.insights')}</Title>
            </Col>
            <Col span={8} className="card-item">
              <Image
                src={JustInImage}
                width={200}
                preview={false}
                onClick={() => onOpenModalFormCard(FormCard.HANDICAP)}
              />
              <Title level={4}>{t('modal.handicap')}</Title>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8} className="card-item">
              <Image
                src={JustInImage}
                width={200}
                preview={false}
                onClick={() => onOpenModalFormCard(FormCard.SCORE)}
              />
              <Title level={4}>{t('modal.scores')}</Title>
            </Col>
            <Col span={8} className="card-item">
              <Image
                src={JustInImage}
                width={200}
                preview={false}
                onClick={() => onOpenModalFormCard(FormCard.TARGET)}
              />
              <Title level={4}>{t('modal.target')}</Title>
            </Col>
          </Row>
        </div>
      )}
      <SpinLoading isLoading={isLoading} absolute>
        <>
          {formCardStage === FormCard.JUST_IN && <FormJustIn onSaveHomeWidgets={onSaveHomeWidgets} record={record} />}
          {formCardStage === FormCard.INSIGHTS && (
            <FormInsights onSaveHomeWidgets={onSaveHomeWidgets} record={record} />
          )}
          {formCardStage === FormCard.HANDICAP && (
            <FormHandicap onSaveHomeWidgets={onSaveHomeWidgets} record={record} />
          )}
          {formCardStage === FormCard.SCORE && <FormScore onSaveHomeWidgets={onSaveHomeWidgets} record={record} />}
          {formCardStage === FormCard.TARGET && <FormTarget onSaveHomeWidgets={onSaveHomeWidgets} record={record} />}
        </>
      </SpinLoading>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  & > .ant-modal-content {
    ${appStyles.commonModalContentStyle}
  }

  .site-card-wrapper {
    ${appStyles.background.modalBgGray}
    & .card-item {
      text-align: center;
      padding: 1rem;
    }
  }

  & h4.ant-typography {
    ${appStyles.color.black}
  }

  .ant-modal-header {
    padding-left: 0;
  }

  .card-item .ant-image {
    cursor: pointer;
  }

  & .card-up-to-date {
    & .ant-image {
      padding: 0px;
      margin-left: 12px;
      & img {
        width: 92%;
        padding: 5px 0 0px 0;
      }
    }
  }
`;
